import { AccountOverview, AccountOverviewRequestArgs, FetchError } from "@decibeltrade/sdk";
import { FetchQueryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { readDexSdk } from "../dex-sdk";
import { useAccountOverviewSubscription } from "../subscriptions/account-overview";

/** Prefix matching every account-overview query for a subaccount (for invalidation). */
export function accountOverviewQueryKeyPrefix(subAddr: string) {
  return ["accountOverview", subAddr] as const;
}

export function accountOverviewQueryKey({
  subAddr,
  volumeWindow,
  includePerformance,
}: AccountOverviewRequestArgs) {
  return [...accountOverviewQueryKeyPrefix(subAddr), volumeWindow, includePerformance] as const;
}

export type CachedAccountOverview = AccountOverview & { isPlaceholder?: true };

export const PLACEHOLDER_ACCOUNT_OVERVIEW: CachedAccountOverview = {
  isPlaceholder: true,
  perp_equity_balance: 0,
  unrealized_pnl: 0,
  unrealized_funding_cost: 0,
  cross_margin_ratio: 0,
  maintenance_margin: 0,
  cross_account_leverage_ratio: 0,
  volume: null,
  total_margin: 0,
  usdc_cross_withdrawable_balance: 0,
  usdc_isolated_withdrawable_balance: 0,
  margin_deficit: 0,
  all_time_return: null,
  pnl_90d: null,
  sharpe_ratio: null,
  max_drawdown: null,
  weekly_win_rate_12w: null,
  average_cash_position: null,
  average_leverage: null,
  cross_account_position: 0,
  realized_pnl: null,
  liquidation_fees_paid: null,
  liquidation_losses: null,
  net_deposits: null,
  vault_equity: null,
  secondary_collateral: null,
};

// @Todo: Avoid use placeholder values, and use static empty states
export function accountOverviewQuery(args: AccountOverviewRequestArgs) {
  return {
    queryKey: accountOverviewQueryKey(args),
    queryFn: async (): Promise<CachedAccountOverview> => {
      if (!args.subAddr) return PLACEHOLDER_ACCOUNT_OVERVIEW;

      try {
        return await readDexSdk.accountOverview.getByAddr(args);
      } catch (error) {
        if (error instanceof FetchError && error.status === 404) {
          return PLACEHOLDER_ACCOUNT_OVERVIEW;
        }
        throw error;
      }
    },
    retry(failureCount, error) {
      if (error instanceof FetchError && error.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  } satisfies FetchQueryOptions;
}

export function useAccountOverview(args: AccountOverviewRequestArgs) {
  useAccountOverviewSubscription(args);
  return useSuspenseQuery(accountOverviewQuery(args));
}

export const useAccountOverviewQuery = (args: AccountOverviewRequestArgs) => {
  // Parity with useAccountOverview — attach WS updates for the conditional variant too.
  useAccountOverviewSubscription(args);
  return useQuery({
    ...accountOverviewQuery(args),
    enabled: !!args.subAddr,
  });
};
